import { App } from '@capacitor/app';
import { AuthService } from 'ionic-appauth';
import { CapacitorBrowser } from 'ionic-appauth/lib/capacitor';
import { isPlatform } from '@ionic/vue';
import { AxiosRequestor } from './AxiosService';
import { LocalStorageBackend } from "@openid/appauth";
import store from "@/store";
import * as Sentry from '@sentry/vue';
export class Auth {
    static authService;
    static redirectUrl = isPlatform('capacitor') ? 'de.degener.campus://callback' : window.location.origin + '/authcallback';
    static endSessionUrl = isPlatform('capacitor') ? 'de.degener.campus://endSession' : window.location.origin + '/endsession';
    static clientId = process.env.VUE_APP_API_CLIENT_ID;
    static baseUrl = process.env.VUE_APP_OPENID_URL;
    static buildAuthInstance() {
        const authService = new AuthService(new CapacitorBrowser(), new LocalStorageBackend(), new AxiosRequestor());
        authService.authConfig = {
            client_id: Auth.clientId,
            server_host: Auth.baseUrl,
            redirect_url: Auth.redirectUrl,
            end_session_redirect_url: Auth.endSessionUrl,
            scopes: 'profile email Campus.Fahrschueler Campus.SignalR roles offline_access openid Fso.Elbe.Api Fso.Cloud.Api cloudapi',
            pkce: true
        };
        if (isPlatform('capacitor')) {
            App.addListener('appUrlOpen', (data) => {
                if ((data.url).indexOf(authService.authConfig.redirect_url) === 0) {
                    authService.authorizationCallback(data.url);
                }
                else {
                    authService.endSessionCallback();
                }
            });
        }
        authService.init();
        return authService;
    }
    static get Instance() {
        if (!this.authService) {
            this.authService = this.buildAuthInstance();
        }
        return this.authService;
    }
    static get tokenValidForSeconds() {
        try {
            const tokenResponse = localStorage.getItem('token_response');
            if (!tokenResponse)
                return 0;
            const { expires_in, issued_at } = JSON.parse(tokenResponse);
            const now = Math.floor(Date.now() / 1000);
            const elapsed = now - issued_at;
            return expires_in - elapsed;
        }
        catch (err) {
            Sentry.captureException(err, {
                tags: { area: 'auth', type: 'invalid-token-response' },
                extra: { raw: localStorage.getItem('token_response') }
            });
            return 0;
        }
    }
    static async getNewAccessTokenForScopes(scopes) {
        const tokenResponse = localStorage.getItem('token_response');
        if (!tokenResponse) {
            const message = '[Auth] ❌ No token_response. Login required.';
            console.error(message);
            throw new Error(message);
        }
        let parsed;
        try {
            parsed = JSON.parse(tokenResponse);
        }
        catch (err) {
            Sentry.captureException(err, {
                tags: { area: 'auth', type: 'json-parse-failed' },
                extra: { tokenResponse }
            });
            throw new Error('Corrupted token_response in storage.');
        }
        const { refresh_token } = parsed;
        if (!refresh_token) {
            const msg = '[Auth] ❌ Missing refresh_token in token_response.';
            console.error(msg);
            Sentry.captureMessage(msg, {
                level: 'error',
                tags: { area: 'auth', type: 'missing-refresh-token' }
            });
            throw new Error(msg);
        }
        const url = `${Auth.baseUrl}/connect/token`;
        const params = {
            client_id: Auth.clientId,
            grant_type: 'refresh_token',
            refresh_token,
            scope: scopes
        };
        console.log('[Auth] 🔄 Requesting new access token for scopes:', scopes);
        let response;
        try {
            response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(params).toString()
            });
        }
        catch (networkErr) {
            Sentry.captureException(networkErr, {
                tags: { area: 'auth', type: 'network-error' },
                extra: { url, scopes }
            });
            throw new Error('Network error while requesting token.');
        }
        if (!response.ok) {
            const text = await response.text();
            console.error('[Auth] ❌ Failed to request new access token:', text);
            Sentry.captureException(new Error('Failed token request'), {
                tags: { area: 'auth', type: 'token-endpoint-failure' },
                extra: { text, scopes, status: response.status }
            });
            throw new Error(text || 'Token request failed.');
        }
        let data;
        try {
            data = await response.json();
        }
        catch (err) {
            Sentry.captureException(err, {
                tags: { area: 'auth', type: 'token-json-parse-failed' }
            });
            throw new Error('Invalid JSON in token response.');
        }
        console.log('[Auth] 🎉 New access token received:', data);
        if (data.refresh_token) {
            console.log('[Auth] 🔁 Refresh token rotated → updating only refresh_token');
            parsed.refresh_token = data.refresh_token;
            if (data.expires_in) {
                parsed.expires_in = data.expires_in;
            }
            parsed.issued_at = Math.floor(Date.now() / 1000);
            localStorage.setItem('token_response', JSON.stringify(parsed));
        }
        return {
            accessToken: data.access_token,
            expiresAt: Math.floor(Date.now() / 1000) + data.expires_in
        };
    }
    static clearUserData() {
        const timestamp = new Date().toISOString();
        console.warn(`[Auth] 🚪 clearUserData() called at ${timestamp}`);
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User logged out',
            level: 'info',
            data: { timestamp }
        });
        // Remove user from Sentry
        Sentry.setUser(null);
        // Store logout timestamp if needed
        localStorage.setItem('lastLogoutTimestamp', timestamp);
        // Clear tokens and app data
        localStorage.removeItem('fcmToken');
        localStorage.removeItem('fcmTokenSaved');
        localStorage.removeItem('cloudApiUrl');
        localStorage.removeItem('tokenJustRefreshed');
        localStorage.removeItem('token_response');
        store.commit('setAuthState', false);
        console.log('Clear user data: isAuthenticated', store.state.isAuthenticated);
        store.commit('setUserInfo', {
            name: null,
            username: null,
            email: null,
            cloudApiUrl: null,
        });
        store.commit('setSchoolLoadingStatus', false);
        store.commit('setSchoolInfo', {
            city: '',
            email: '',
            facebook: '',
            instagram: '',
            logo: '',
            name: '',
            phone: '',
            snapChat: '',
            street: '',
            tikTok: '',
            website: '',
            youtube: '',
            zipCode: ''
        });
        console.log(`[Auth] 🧹 Local storage after logout:`, { ...localStorage });
    }
}
